import { useState, useCallback, useEffect, useRef } from 'react';
import { Grid, GameState, DiceValue, Difficulty, HistoryEntry, Outcome } from '../types';
import { INITIAL_GRID, SPEED_MAP, LAST_ACTION_HIGHLIGHT_MS } from '../constants';
import { calculateTotalScore, isGridFull, getBestMove } from '../utils';
import { useSound } from './useSound';
import { trackEvent } from '../utils/analytics';
import { useTranslation } from '../i18n';

export const useGame = (difficulty: Difficulty, isMuted: boolean, diceSpeed: string, onGameEnd?: (result: { outcome: Outcome; playerScore: number; opponentScore: number; entry: HistoryEntry }) => void, turnTimeout: number | null = null) => {
  const t = useTranslation();
  const { triggerSound } = useSound(isMuted);
  const [playerGrid, setPlayerGrid] = useState<Grid>(INITIAL_GRID);
  const [opponentGrid, setOpponentGrid] = useState<Grid>(INITIAL_GRID);
  const [currentDie, setCurrentDie] = useState<DiceValue | null>(null);
  const [isDieRolling, setIsDieRolling] = useState(false);
  const [gameState, setGameState] = useState<GameState>('IDLE');
  const [message, setMessage] = useState<string>("");
  const [lastAction, setLastAction] = useState<{col: number, target: 'player' | 'opponent'} | null>(null);
  const [turnTimer, setTurnTimer] = useState<number | null>(null);
  
  // Track if game is active to prevent auto-rolling after reset
  const isGameActiveRef = useRef(false);
  // Track active timeouts for cleanup on unmount
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const turnTimerPlacedRef = useRef(false);

  const addTimer = (timer: ReturnType<typeof setTimeout>) => {
    timersRef.current.push(timer);
  };

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  const playerTotal = calculateTotalScore(playerGrid);
  const opponentTotal = calculateTotalScore(opponentGrid);

  const resetGrids = useCallback(() => {
    isGameActiveRef.current = false;
    setPlayerGrid(INITIAL_GRID);
    setOpponentGrid(INITIAL_GRID);
    setCurrentDie(null);
    setIsDieRolling(false);
    setGameState('IDLE');
    setMessage("");
    setLastAction(null);
  }, []);

  const rollDie = useCallback(() => {
    setIsDieRolling(true);
    setMessage(t.msgRolling);
    setGameState('PLAYER_ROLLING');
    triggerSound('roll');
    
    const rollDuration = 800 * SPEED_MAP[diceSpeed as keyof typeof SPEED_MAP];
    addTimer(setTimeout(() => {
      const val = (Math.floor(Math.random() * 6) + 1) as DiceValue;
      setCurrentDie(val);
      setIsDieRolling(false);
      setGameState('PLAYER_PLACING');
      setMessage(t.msgYourTurn);
    }, rollDuration));
  }, [triggerSound, diceSpeed, t]);

  const placePlayerDie = (colIdx: number) => {
    if (gameState !== 'PLAYER_PLACING' || !currentDie) return;

    const val = currentDie;
    const newPlayerGrid = [...playerGrid];
    const col = [...newPlayerGrid[colIdx]];
    const emptyIdx = col.indexOf(null);
    
    if (emptyIdx === -1) return;

    col[emptyIdx] = val;
    newPlayerGrid[colIdx] = col as (DiceValue | null)[];
    setPlayerGrid(newPlayerGrid);
    triggerSound('place');

    const newOpponentGrid = [...opponentGrid];
    const oCol = [...newOpponentGrid[colIdx]];
    const hasMatch = oCol.includes(val);
    
    if (hasMatch) {
      const updatedOCol = oCol.filter(v => v !== val);
      while (updatedOCol.length < 3) updatedOCol.push(null);
      newOpponentGrid[colIdx] = updatedOCol as (DiceValue | null)[];
      
      setMessage(t.msgSlaughtered);
      triggerSound('destroy');
      setLastAction({ col: colIdx, target: 'opponent' });
      addTimer(setTimeout(() => setLastAction(null), LAST_ACTION_HIGHLIGHT_MS));
    }

    setOpponentGrid(newOpponentGrid);
    return checkGameEnd(newPlayerGrid, newOpponentGrid, 'OPPONENT_THINKING');
  };

  const opponentTurn = useCallback(() => {
    setIsDieRolling(true);
    setMessage(t.msgLordRolling);
    setGameState('OPPONENT_THINKING');
    triggerSound('roll');
    
    const speed = SPEED_MAP[diceSpeed as keyof typeof SPEED_MAP];
    
    addTimer(setTimeout(() => {
      const val = (Math.floor(Math.random() * 6) + 1) as DiceValue;
      setCurrentDie(val);
      setIsDieRolling(false);
      setGameState('OPPONENT_PLACING');
      
      addTimer(setTimeout(() => {
        const bestCol = getBestMove(opponentGrid, playerGrid, val, difficulty);
        const newOpponentGrid = [...opponentGrid];
        const col = [...newOpponentGrid[bestCol]];
        const emptyIdx = col.indexOf(null);
        col[emptyIdx] = val;
        newOpponentGrid[bestCol] = col as (DiceValue | null)[];
        setOpponentGrid(newOpponentGrid);
        triggerSound('place');

        const newPlayerGrid = [...playerGrid];
        const pCol = [...newPlayerGrid[bestCol]];
        const updatedPCol = pCol.filter(v => v !== val);
        while (updatedPCol.length < 3) updatedPCol.push(null);
        newPlayerGrid[bestCol] = updatedPCol as (DiceValue | null)[];
        
        if (pCol.includes(val)) {
          setMessage(t.msgCrushed);
          triggerSound('destroy');
          setLastAction({ col: bestCol, target: 'player' });
          addTimer(setTimeout(() => setLastAction(null), LAST_ACTION_HIGHLIGHT_MS));
        }

        setPlayerGrid(newPlayerGrid);
        return checkGameEnd(newPlayerGrid, newOpponentGrid, 'PLAYER_ROLLING');
      }, 1000 * speed));
    }, 800 * speed));
  }, [opponentGrid, playerGrid, difficulty, diceSpeed, triggerSound, t]);

  const checkGameEnd = (pGrid: Grid, oGrid: Grid, nextState: GameState) => {
    if (isGridFull(pGrid) || isGridFull(oGrid)) {
      setGameState('GAME_OVER');
      const pTotal = calculateTotalScore(pGrid);
      const oTotal = calculateTotalScore(oGrid);
      
      let outcome: Outcome = 'TIE';
      if (pTotal > oTotal) {
        outcome = 'VICTORY';
        setMessage(t.msgVictory);
        triggerSound('win');
      } else if (oTotal > pTotal) {
        outcome = 'DEFEAT';
        setMessage(t.msgDefeat);
        triggerSound('lose');
      } else {
        setMessage(t.msgTie);
      }

      const newEntry: HistoryEntry = {
        id: Date.now().toString(),
        playerScore: pTotal,
        opponentScore: oTotal,
        outcome,
        difficulty,
        date: new Date().toLocaleDateString()
      };

      trackEvent('practice_game_over', { outcome, difficulty, player_score: pTotal, opponent_score: oTotal });

      if (onGameEnd) {
        onGameEnd({ outcome, playerScore: pTotal, opponentScore: oTotal, entry: newEntry });
      }

      return { outcome, entry: newEntry, playerScore: pTotal, opponentScore: oTotal };
    } else {
      setGameState(nextState);
      if (nextState === 'START') {
        setCurrentDie(null);
        setMessage(t.msgReadyToRoll);
      }
      return null;
    }
  };

  useEffect(() => {
    // Only auto-start if game is active
    if (!isGameActiveRef.current) return;

    if (gameState === 'START' || gameState === 'PLAYER_ROLLING') {
      const timer = setTimeout(() => {
        if (isGameActiveRef.current) {
          rollDie();
        }
      }, 800);
      return () => clearTimeout(timer);
    }

    // Trigger opponent turn when it's their time
    if (gameState === 'OPPONENT_THINKING') {
      const timer = setTimeout(() => {
        if (isGameActiveRef.current) {
          opponentTurn();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [gameState, rollDie, opponentTurn]);

  // ── Turn timer for practice mode with timeout ─────────────────────────
  const placePlayerDieRef = useRef(placePlayerDie);
  placePlayerDieRef.current = placePlayerDie;

  useEffect(() => {
    if (!turnTimeout || gameState !== 'PLAYER_PLACING') {
      setTurnTimer(null);
      turnTimerPlacedRef.current = false;
      return;
    }

    // Start the timer at the configured seconds
    setTurnTimer(turnTimeout);
    turnTimerPlacedRef.current = false;

    // Tick sound at start
    triggerSound('tick');

    let current = turnTimeout;
    const interval = setInterval(() => {
      current -= 1;
      setTurnTimer(current);

      if (current === 1) {
        triggerSound('tickUrgent');
      } else if (current > 0) {
        triggerSound('tick');
      }

      if (current <= 0) {
        clearInterval(interval);

        // Time's up -- auto-place at a random valid column
        if (!turnTimerPlacedRef.current) {
          turnTimerPlacedRef.current = true;
          triggerSound('timeout');

          const validCols = playerGrid
            .map((col, idx) => ({ idx, hasSpace: col.filter((v) => v !== null).length < 3 }))
            .filter((c) => c.hasSpace)
            .map((c) => c.idx);

          if (validCols.length > 0) {
            const randomCol = validCols[Math.floor(Math.random() * validCols.length)];
            console.log('[Practice] Turn timer expired! Auto-placing at column:', randomCol);
            placePlayerDieRef.current(randomCol);
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameState, turnTimeout, triggerSound, playerGrid]);

  // Start game when entering GAME view
  const startGame = useCallback(() => {
    isGameActiveRef.current = true;
    setGameState('START');
    setMessage(t.msgBattleBegins);
  }, [t]);

  return {
    playerGrid,
    opponentGrid,
    currentDie,
    isDieRolling,
    gameState,
    message,
    lastAction,
    playerTotal,
    opponentTotal,
    resetGrids,
    rollDie,
    placePlayerDie,
    opponentTurn,
    setGameState,
    setMessage,
    setCurrentDie,
    startGame,
    turnTimer,
  };
};
